import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Check, ClipboardCheck, Plus, Search, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import {
  ADJUSTMENT_REASONS,
  ADJUSTMENT_REASON_LABELS,
  useInventoryLevels,
  useItemKinds,
  useItems,
  useLocations,
  useOnOrderByItem,
  useStockedUnitByItem,
  type AdjustmentReason,
  type Item,
  type ItemOnOrder,
} from '@/features/inventory/hooks';
import {
  buildCycleCountLines,
  countedBaseQty,
  cycleCountLineKey,
  loadCycleCountDraft,
  newCycleCountDraft,
  saveCycleCountDraft,
  usePostCycleCount,
  type CountUnit,
  type CycleCountAdjustment,
  type CycleCountDraft,
  type CycleCountEntry,
  type CycleCountLine,
} from '@/features/inventory/cycleCount';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { formatError } from '@/lib/errors';
import { formatDateOnly, formatDateTime } from '@/lib/formatters';
import { formatInStockedUnit, formatQty, formatQtyUom, type PurchaseUnit } from '@/lib/uom';

type LineFilter = 'all' | 'todo' | 'counted' | 'variance';

const FILTERS: { value: LineFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'todo', label: 'To count' },
  { value: 'counted', label: 'Counted' },
  { value: 'variance', label: 'Variances' },
];

const ALL_KINDS = '__all__';

interface LineState {
  counted: number | null;
  invalid: boolean;
  delta: number | null;
}

function lineState(entry: CycleCountEntry | undefined, unit: PurchaseUnit | undefined): LineState {
  const counted = countedBaseQty(entry, unit?.unitsPerPurchaseUom ?? 1);
  const invalid = !!entry && entry.counted.trim() !== '' && counted == null;
  const delta = counted != null && entry ? counted - entry.systemAtCount : null;
  return { counted, invalid, delta };
}

/** A change of half the system quantity or more is usually a typo or a miscount. */
function isLargeVariance(delta: number | null, system: number): boolean {
  return !!delta && system !== 0 && Math.abs(delta) >= Math.abs(system) * 0.5;
}

function signed(n: number): string {
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${formatQty(Math.abs(n))}`;
}

function focusNextCountInput(current: HTMLInputElement) {
  const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[data-count-input]'));
  const next = inputs[inputs.indexOf(current) + 1];
  if (next) {
    next.focus();
    next.select();
  } else {
    current.blur();
  }
}

/**
 * Physical cycle count. Pick the location being counted, then walk the
 * shelves: every active item is listed with its system on-hand and what's
 * on order, and the counter types what is physically there. Lines that
 * don't match need a reason. Nothing changes on-hand until the count is
 * reviewed and posted, which writes one `cycle_count` movement per
 * variance to the Adjustments ledger. Progress is kept on this device.
 */
export function CycleCountPage() {
  const navigate = useNavigate();
  const { role, profile, user } = useAuth();
  const orgId = profile?.organization_id;
  const userId = user?.id;
  const canManage = canWrite(role, 'inventory');

  const { data: items, isLoading: itemsLoading } = useItems();
  const { data: levels, isLoading: levelsLoading } = useInventoryLevels(undefined, {
    refetchInterval: 60_000,
  });
  const { data: locations, isLoading: locationsLoading } = useLocations();
  const { data: onOrderByItem } = useOnOrderByItem();
  const { data: stockedUnitByItem } = useStockedUnitByItem();
  const { data: kinds } = useItemKinds();
  const postCount = usePostCycleCount();

  const [draft, setDraft] = useState<CycleCountDraft | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [startLocationId, setStartLocationId] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<LineFilter>('all');
  const [kindFilter, setKindFilter] = useState(ALL_KINDS);
  const [onlyStockedHere, setOnlyStockedHere] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [addLotOpen, setAddLotOpen] = useState(false);

  const storageId = orgId && userId ? `${orgId}:${userId}` : null;
  if (storageId && loadedFor !== storageId) {
    setLoadedFor(storageId);
    setDraft(loadCycleCountDraft(orgId!, userId!));
  }

  useEffect(() => {
    if (!orgId || !userId || loadedFor !== `${orgId}:${userId}`) return;
    saveCycleCountDraft(orgId, userId, draft);
  }, [draft, orgId, userId, loadedFor]);

  const locationRows = useMemo(() => locations ?? [], [locations]);
  const location = draft ? (locationRows.find((l) => l.id === draft.locationId) ?? null) : null;

  // A single location needs no picking.
  useEffect(() => {
    if (!draft && loadedFor && locationRows.length === 1) {
      setDraft(newCycleCountDraft(locationRows[0].id));
    }
  }, [draft, loadedFor, locationRows]);

  const draftLocationId = draft?.locationId;
  const draftAddedLines = draft?.addedLines;
  const lines = useMemo(
    () =>
      draftLocationId
        ? buildCycleCountLines(items ?? [], levels ?? [], draftLocationId, draftAddedLines ?? [])
        : [],
    [items, levels, draftLocationId, draftAddedLines],
  );

  const kindLabels = useMemo(() => new Map((kinds ?? []).map((k) => [k.slug, k.label])), [kinds]);
  const kindOptions = useMemo(() => {
    const used = new Set(lines.flatMap((l) => l.kinds));
    return (kinds ?? []).filter((k) => used.has(k.slug));
  }, [kinds, lines]);

  const states = useMemo(() => {
    const map = new Map<string, LineState>();
    for (const line of lines) {
      map.set(line.key, lineState(draft?.entries[line.key], stockedUnitByItem?.get(line.itemId)));
    }
    return map;
  }, [lines, draft, stockedUnitByItem]);

  const stats = useMemo(() => {
    let counted = 0;
    let variances = 0;
    let invalid = 0;
    let net = 0;
    for (const line of lines) {
      const s = states.get(line.key);
      if (!s) continue;
      if (s.invalid) invalid += 1;
      if (s.counted == null) continue;
      counted += 1;
      if (s.delta) {
        variances += 1;
        net += s.delta;
      }
    }
    return { total: lines.length, counted, variances, invalid, net };
  }, [lines, states]);

  const visibleLines = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lines.filter((line) => {
      const s = states.get(line.key);
      const isCounted = s?.counted != null;
      if (onlyStockedHere && line.notStockedHere && !line.added && !isCounted) return false;
      if (kindFilter !== ALL_KINDS && !line.kinds.includes(kindFilter)) return false;
      if (filter === 'todo' && isCounted) return false;
      if (filter === 'counted' && !isCounted) return false;
      if (filter === 'variance' && !s?.delta) return false;
      if (!q) return true;
      return (
        line.sku.toLowerCase().includes(q) ||
        line.name.toLowerCase().includes(q) ||
        (line.lotNumber ?? '').toLowerCase().includes(q)
      );
    });
  }, [lines, states, search, filter, kindFilter, onlyStockedHere]);

  const updateEntry = useCallback((line: CycleCountLine, patch: Partial<CycleCountEntry>) => {
    setDraft((d) => {
      if (!d) return d;
      const prev: CycleCountEntry = d.entries[line.key] ?? {
        counted: '',
        unit: 'base',
        systemAtCount: line.onHand,
        reason: 'count_correction',
        note: '',
      };
      const next: CycleCountEntry = { ...prev, ...patch };
      if (patch.counted !== undefined) next.systemAtCount = line.onHand;
      return { ...d, entries: { ...d.entries, [line.key]: next } };
    });
  }, []);

  const clearEntry = useCallback((line: CycleCountLine) => {
    setDraft((d) => {
      if (!d || !d.entries[line.key]) return d;
      const entries = { ...d.entries };
      delete entries[line.key];
      return { ...d, entries };
    });
  }, []);

  const removeAddedLine = useCallback((line: CycleCountLine) => {
    setDraft((d) => {
      if (!d) return d;
      const entries = { ...d.entries };
      delete entries[line.key];
      return {
        ...d,
        entries,
        addedLines: d.addedLines.filter(
          (a) => !(a.itemId === line.itemId && a.lotNumber === line.lotNumber),
        ),
      };
    });
  }, []);

  const discard = () => {
    const started = stats.counted > 0 || (draft?.addedLines.length ?? 0) > 0;
    if (
      started &&
      !window.confirm(
        stats.counted > 0
          ? `Discard this count? ${stats.counted === 1 ? 'The count' : `The ${stats.counted.toLocaleString()} counts`} entered so far will be lost.`
          : 'Discard this count?',
      )
    ) {
      return;
    }
    if (orgId && userId) saveCycleCountDraft(orgId, userId, null);
    setDraft(null);
    setSearch('');
    setFilter('all');
    setReviewOpen(false);
  };

  const adjustments = useMemo<CycleCountAdjustment[]>(() => {
    if (!draft) return [];
    const out: CycleCountAdjustment[] = [];
    for (const line of lines) {
      const s = states.get(line.key);
      const entry = draft.entries[line.key];
      if (!s?.delta || s.counted == null || !entry) continue;
      const memo = [
        `Counted ${formatQtyUom(s.counted, line.baseUom)}, system ${formatQty(entry.systemAtCount)}`,
        entry.note.trim(),
        draft.note.trim(),
      ]
        .filter(Boolean)
        .join(' · ');
      out.push({
        key: line.key,
        itemId: line.itemId,
        locationId: line.locationId,
        lotNumber: line.lotNumber,
        delta: s.delta,
        reason: entry.reason,
        memo,
      });
    }
    return out;
  }, [draft, lines, states]);

  const finish = (posted: number) => {
    // Cleared here rather than by the save effect, which won't run once
    // the navigation below unmounts the page.
    if (orgId && userId) saveCycleCountDraft(orgId, userId, null);
    setDraft(null);
    setReviewOpen(false);
    toast.success(
      posted > 0
        ? `Cycle count posted: ${posted.toLocaleString()} adjustment${posted === 1 ? '' : 's'}.`
        : 'Cycle count finished. Everything matched.',
    );
    navigate('/inventory/adjustments');
  };

  const post = async () => {
    if (!draft) return;
    if (adjustments.length === 0) {
      finish(0);
      return;
    }
    try {
      const { posted } = await postCount.mutateAsync({
        countId: draft.id,
        adjustments,
        onPosted: (key) =>
          setDraft((d) => {
            if (!d) return d;
            const entries = { ...d.entries };
            delete entries[key];
            return { ...d, entries };
          }),
      });
      finish(posted);
    } catch (err) {
      toast.error(
        formatError(err, 'Could not post the cycle count.') +
          ' Lines already posted were removed from the sheet; post again to finish the rest.',
      );
    }
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <PageHeader title="Cycle count" />
        <EmptyState icon={ClipboardCheck} title="You don't have access to post inventory counts" />
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Cycle count"
          actions={
            <Button variant="ghost" asChild>
              <Link to="/inventory/adjustments">Back to adjustments</Link>
            </Button>
          }
        />
        <Card className="mx-auto max-w-lg">
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-1">
              <h2 className="text-base font-medium text-foreground">Start a count</h2>
              <p className="text-sm text-muted-foreground">
                Pick the location you're counting. Every item is listed with what the system says is
                on hand there; type what you physically find. Nothing changes until you review and
                post.
              </p>
            </div>
            {locationsLoading ? (
              <p className="text-sm text-muted-foreground">Loading locations…</p>
            ) : locationRows.length === 0 ? (
              <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-sm text-muted-foreground">
                No active locations yet.{' '}
                <Link to="/inventory/locations" className="underline">
                  Add one
                </Link>{' '}
                first.
              </p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="cycle-count-location">Location</Label>
                  <Select value={startLocationId} onValueChange={setStartLocationId}>
                    <SelectTrigger id="cycle-count-location" className="h-12">
                      <SelectValue placeholder="Pick a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locationRows.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="lg"
                  className="w-full"
                  disabled={!startLocationId}
                  onClick={() => setDraft(newCycleCountDraft(startLocationId))}
                >
                  <ClipboardCheck className="h-4 w-4" />
                  Start count
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const loading = itemsLoading || levelsLoading;
  const hasLotTracked = (items ?? []).some((it) => it.is_lot_tracked);
  const pct = stats.total > 0 ? Math.round((stats.counted / stats.total) * 100) : 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cycle count"
        className="pb-2"
        meta={
          <>
            {location ? `${location.name} (${location.code})` : 'Unknown location'} · started{' '}
            {formatDateTime(draft.startedAt)}
          </>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {hasLotTracked && (
              <Button variant="outline" onClick={() => setAddLotOpen(true)}>
                <Plus className="h-4 w-4" />
                Add lot
              </Button>
            )}
            <Button variant="ghost" onClick={discard}>
              <Trash2 className="h-4 w-4" />
              Discard
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="space-y-3 pt-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="text-foreground">
              <span className="font-medium tabular-nums">{stats.counted.toLocaleString()}</span> of{' '}
              <span className="tabular-nums">{stats.total.toLocaleString()}</span> counted
            </span>
            <span className="flex gap-4 text-muted-foreground">
              <span>
                <span className="tabular-nums text-foreground">
                  {stats.variances.toLocaleString()}
                </span>{' '}
                variance
                {stats.variances === 1 ? '' : 's'}
              </span>
              <span>
                Net{' '}
                <span
                  className={cn(
                    'tabular-nums',
                    stats.net > 0 && 'text-bright-moss-dark',
                    stats.net < 0 && 'text-fresh-salmon-dark',
                    stats.net === 0 && 'text-foreground',
                  )}
                >
                  {signed(stats.net)}
                </span>
              </span>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary" aria-hidden>
            <div
              className="h-full rounded-full bg-bright-moss-dark transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="flex flex-col gap-3 pt-1 md:flex-row md:items-center">
            <div className="flex h-12 flex-1 items-center gap-2 rounded-lg border border-input bg-card px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search SKU, item, or lot…"
                className="h-full w-full bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {kindOptions.length > 1 && (
              <Select value={kindFilter} onValueChange={setKindFilter}>
                <SelectTrigger className="h-12 md:w-48" aria-label="Item type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_KINDS}>All types</SelectItem>
                  {kindOptions.map((k) => (
                    <SelectItem key={k.slug} value={k.slug}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-border p-1" role="tablist">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.value}
                  onClick={() => setFilter(f.value)}
                  className={cn(
                    'min-h-[40px] rounded-md px-3 text-sm transition-colors',
                    filter === f.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="only-stocked-here"
                checked={onlyStockedHere}
                onCheckedChange={setOnlyStockedHere}
              />
              <Label
                htmlFor="only-stocked-here"
                className="cursor-pointer text-sm text-muted-foreground"
              >
                Only items stocked here
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div className="hidden border-b border-border bg-secondary/40 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground lg:flex lg:items-center lg:gap-4">
          <span className="flex-1">Item</span>
          <span className="w-24 text-right">On hand</span>
          <span className="w-24 text-right">On order</span>
          <span className="w-[360px] pl-1">Counted</span>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading items…</div>
        ) : visibleLines.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {lines.length === 0 ? 'No active items to count.' : 'No lines match.'}
          </div>
        ) : (
          visibleLines.map((line) => (
            <CountRow
              key={line.key}
              line={line}
              entry={draft.entries[line.key]}
              state={states.get(line.key)!}
              onOrder={onOrderByItem?.get(line.itemId)}
              stockedUnit={stockedUnitByItem?.get(line.itemId)}
              kindLabel={kindLabels.get(line.kinds[0]) ?? null}
              onChange={updateEntry}
              onClear={clearEntry}
              onRemove={removeAddedLine}
            />
          ))
        )}
      </Card>

      <div className="sticky bottom-0 z-10 -mx-4 border-t border-border bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            {stats.invalid > 0 ? (
              <span className="text-fresh-salmon-dark">
                {stats.invalid.toLocaleString()} count{stats.invalid === 1 ? '' : 's'} not a valid
                number
              </span>
            ) : (
              <>
                <span className="tabular-nums text-foreground">
                  {stats.counted.toLocaleString()}
                </span>{' '}
                counted ·{' '}
                <span className="tabular-nums text-foreground">
                  {stats.variances.toLocaleString()}
                </span>{' '}
                to adjust
              </>
            )}
          </div>
          <Button
            size="lg"
            onClick={() => setReviewOpen(true)}
            disabled={stats.counted === 0 || stats.invalid > 0}
          >
            <ClipboardCheck className="h-4 w-4" />
            Review & post
          </Button>
        </div>
      </div>

      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        lines={lines}
        states={states}
        draft={draft}
        adjustments={adjustments}
        uncounted={stats.total - stats.counted}
        matched={stats.counted - stats.variances}
        onNoteChange={(note) => setDraft((d) => (d ? { ...d, note } : d))}
        onPost={post}
        posting={postCount.isPending}
      />

      <AddLotDialog
        open={addLotOpen}
        onOpenChange={setAddLotOpen}
        onAdd={(item, lotNumber) => {
          const key = cycleCountLineKey(item.id, draft.locationId, lotNumber);
          if (lines.some((l) => l.key === key)) {
            toast.info(`Lot ${lotNumber} of ${item.sku} is already on the sheet.`);
          } else {
            setDraft((d) =>
              d ? { ...d, addedLines: [...d.addedLines, { itemId: item.id, lotNumber }] } : d,
            );
          }
          setFilter('all');
          setSearch(lotNumber);
          setAddLotOpen(false);
        }}
      />
    </div>
  );
}

interface CountRowProps {
  line: CycleCountLine;
  entry: CycleCountEntry | undefined;
  state: LineState;
  onOrder: ItemOnOrder | undefined;
  stockedUnit: PurchaseUnit | undefined;
  kindLabel: string | null;
  onChange: (line: CycleCountLine, patch: Partial<CycleCountEntry>) => void;
  onClear: (line: CycleCountLine) => void;
  onRemove: (line: CycleCountLine) => void;
}

const CountRow = memo(function CountRow({
  line,
  entry,
  state,
  onOrder,
  stockedUnit,
  kindLabel,
  onChange,
  onClear,
  onRemove,
}: CountRowProps) {
  const { counted, invalid, delta } = state;
  const unit: CountUnit = entry?.unit ?? 'base';
  const stockedUom = stockedUnit?.purchaseUom?.trim();
  const onHandStocked = formatInStockedUnit(line.onHand, stockedUnit);
  const movedSinceCount = entry && counted != null ? line.onHand - entry.systemAtCount : 0;

  return (
    <div
      className={cn(
        'border-b border-border px-4 py-3 last:border-b-0',
        counted != null && !delta && 'bg-bright-moss/10',
        !!delta && 'bg-fresh-salmon/10',
      )}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="min-w-0 break-words font-medium text-foreground">{line.name}</span>
              {line.added && (
                <button
                  type="button"
                  onClick={() => onRemove(line)}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                  aria-label="Remove this lot from the sheet"
                  title="Remove from sheet"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <Link
                to={`/catalog/items/${line.itemId}?tab=inventory`}
                className="font-mono text-foreground hover:underline"
              >
                {line.sku}
              </Link>
              {line.lotNumber && <span className="font-mono">Lot {line.lotNumber}</span>}
              {line.isLotTracked && !line.lotNumber && <span>No lot</span>}
              {kindLabel && <span>{kindLabel}</span>}
              {line.added ? (
                <Badge variant="info-soft">New lot</Badge>
              ) : (
                line.notStockedHere && <Badge variant="muted">Not stocked here</Badge>
              )}
            </div>
          </div>
          <div className="w-20 shrink-0 text-right lg:w-24">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground lg:hidden">
              On hand
            </div>
            <div className={cn('tabular-nums', line.onHand < 0 && 'text-fresh-salmon-dark')}>
              {formatQty(line.onHand)}
              <span className="ml-1 text-xs text-muted-foreground">{line.baseUom}</span>
            </div>
            {onHandStocked && (
              <div className="text-[11px] text-muted-foreground">{onHandStocked}</div>
            )}
          </div>
          <div className="w-20 shrink-0 text-right lg:w-24">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground lg:hidden">
              On order
            </div>
            {onOrder ? (
              <>
                <div className="tabular-nums">
                  {formatQty(onOrder.quantity)}
                  <span className="ml-1 text-xs text-muted-foreground">{line.baseUom}</span>
                </div>
                {onOrder.nextArrival && (
                  <div className="text-[11px] text-muted-foreground">
                    Due {formatDateOnly(onOrder.nextArrival)}
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted-foreground">—</div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 lg:w-[360px]">
          <div className="relative flex-1">
            <Input
              data-count-input
              value={entry?.counted ?? ''}
              onChange={(e) => onChange(line, { counted: e.target.value })}
              onFocus={(e) => e.currentTarget.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  focusNextCountInput(e.currentTarget);
                }
              }}
              inputMode="decimal"
              enterKeyHint="next"
              autoComplete="off"
              placeholder="Count"
              aria-label={`Counted quantity for ${line.sku}${line.lotNumber ? ` lot ${line.lotNumber}` : ''}`}
              aria-invalid={invalid || undefined}
              className={cn(
                'h-12 pr-10 text-lg tabular-nums',
                invalid && 'border-fresh-salmon-dark focus-visible:ring-fresh-salmon-dark',
              )}
            />
            {entry?.counted && (
              <button
                type="button"
                onClick={() => onClear(line)}
                className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                aria-label="Clear count"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {stockedUom ? (
            <div className="inline-flex h-12 shrink-0 rounded-lg border border-border p-1">
              {(['base', 'stocked'] as const).map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => onChange(line, { unit: u })}
                  className={cn(
                    'min-w-[44px] rounded-md px-2 text-sm',
                    unit === u
                      ? 'bg-secondary font-medium text-foreground'
                      : 'text-muted-foreground',
                  )}
                  aria-pressed={unit === u}
                >
                  {u === 'base' ? line.baseUom : stockedUom}
                </button>
              ))}
            </div>
          ) : (
            <span className="w-10 shrink-0 text-sm text-muted-foreground">{line.baseUom}</span>
          )}
          <Button
            type="button"
            variant="outline"
            className="h-12 w-12 shrink-0 px-0"
            onClick={() =>
              onChange(line, { counted: String(Math.max(0, line.onHand)), unit: 'base' })
            }
            aria-label="Count matches on hand"
            title="Matches on hand"
          >
            <Check className="h-5 w-5" />
          </Button>
          <div className="w-16 shrink-0 text-right tabular-nums">
            {counted == null ? (
              <span className="text-muted-foreground">—</span>
            ) : delta ? (
              <span
                className={cn(
                  'font-medium',
                  delta > 0 ? 'text-bright-moss-dark' : 'text-fresh-salmon-dark',
                )}
              >
                {signed(delta)}
              </span>
            ) : (
              <span className="text-bright-moss-dark">OK</span>
            )}
          </div>
        </div>
      </div>

      {(!!delta || movedSinceCount !== 0) && entry && (
        <div className="mt-3 space-y-2 lg:pl-0">
          {!!delta && (
            <div className="grid gap-2 sm:grid-cols-[220px_minmax(0,1fr)]">
              <Select
                value={entry.reason}
                onValueChange={(v) => onChange(line, { reason: v as AdjustmentReason })}
              >
                <SelectTrigger className="h-11" aria-label="Reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ADJUSTMENT_REASON_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={entry.note}
                onChange={(e) => onChange(line, { note: e.target.value })}
                placeholder="Note (optional) — e.g. two cartons crushed"
                maxLength={500}
                className="h-11"
              />
            </div>
          )}
          {isLargeVariance(delta, entry.systemAtCount) && (
            <p className="flex items-center gap-1.5 text-xs text-fresh-salmon-dark">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Large difference from the system. Double-check the count
              {stockedUom ? ' and the unit' : ''}.
            </p>
          )}
          {movedSinceCount !== 0 && (
            <p className="text-xs text-muted-foreground">
              System on-hand moved {signed(movedSinceCount)} since this was counted (now{' '}
              {formatQty(line.onHand)}). Only the counted difference of {signed(delta ?? 0)} will be
              posted, so that movement is kept.
            </p>
          )}
        </div>
      )}
    </div>
  );
}, sameRowProps);

function sameRowProps(a: CountRowProps, b: CountRowProps): boolean {
  return (
    a.line === b.line &&
    a.entry === b.entry &&
    a.state.counted === b.state.counted &&
    a.state.invalid === b.state.invalid &&
    a.state.delta === b.state.delta &&
    a.onOrder === b.onOrder &&
    a.stockedUnit === b.stockedUnit &&
    a.kindLabel === b.kindLabel &&
    a.onChange === b.onChange &&
    a.onClear === b.onClear &&
    a.onRemove === b.onRemove
  );
}

interface ReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lines: CycleCountLine[];
  states: Map<string, LineState>;
  draft: CycleCountDraft;
  adjustments: CycleCountAdjustment[];
  uncounted: number;
  matched: number;
  onNoteChange: (note: string) => void;
  onPost: () => void;
  posting: boolean;
}

function ReviewDialog({
  open,
  onOpenChange,
  lines,
  states,
  draft,
  adjustments,
  uncounted,
  matched,
  onNoteChange,
  onPost,
  posting,
}: ReviewDialogProps) {
  const byKey = useMemo(() => new Map(lines.map((l) => [l.key, l])), [lines]);
  const net = adjustments.reduce((acc, a) => acc + a.delta, 0);
  const large = adjustments.filter((a) =>
    isLargeVariance(a.delta, draft.entries[a.key]?.systemAtCount ?? 0),
  ).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !posting && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review cycle count</DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md border border-border px-2 py-2">
              <div className="text-lg font-medium tabular-nums">
                {adjustments.length.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">to adjust</div>
            </div>
            <div className="rounded-md border border-border px-2 py-2">
              <div className="text-lg font-medium tabular-nums">{matched.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">matched</div>
            </div>
            <div className="rounded-md border border-border px-2 py-2">
              <div className="text-lg font-medium tabular-nums">{uncounted.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">not counted</div>
            </div>
          </div>

          {uncounted > 0 && (
            <p className="flex items-start gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {uncounted.toLocaleString()} line{uncounted === 1 ? ' was' : 's were'} left blank and
              will stay as they are.
            </p>
          )}

          {large > 0 && (
            <p className="flex items-start gap-2 rounded-md border border-fresh-salmon-dark/40 bg-fresh-salmon/10 px-3 py-2 text-xs text-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fresh-salmon-dark" />
              {large.toLocaleString()} line{large === 1 ? ' differs' : 's differ'} from the system
              by half or more. Double-check {large === 1 ? 'it' : 'them'} before posting.
            </p>
          )}

          {adjustments.length > 0 ? (
            <div className="divide-y divide-border rounded-md border border-border">
              {adjustments.map((adj) => {
                const line = byKey.get(adj.key);
                const entry = draft.entries[adj.key];
                const s = states.get(adj.key);
                if (!line || !entry || !s) return null;
                return (
                  <div
                    key={adj.key}
                    className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">{line.name}</span>
                        {isLargeVariance(adj.delta, entry.systemAtCount) && (
                          <Badge variant="warning-soft" className="shrink-0">
                            Check
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                        <span className="font-mono">{line.sku}</span>
                        {line.lotNumber && <span className="font-mono">Lot {line.lotNumber}</span>}
                        <span>
                          {formatQty(entry.systemAtCount)} → {formatQty(s.counted ?? 0)}{' '}
                          {line.baseUom}
                        </span>
                        <span>· {ADJUSTMENT_REASON_LABELS[entry.reason]}</span>
                      </div>
                      {entry.note.trim() && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {entry.note.trim()}
                        </div>
                      )}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 font-medium tabular-nums',
                        adj.delta > 0 ? 'text-bright-moss-dark' : 'text-fresh-salmon-dark',
                      )}
                    >
                      {signed(adj.delta)}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        {line.baseUom}
                      </span>
                    </span>
                  </div>
                );
              })}
              <div className="flex justify-between bg-secondary/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Net change</span>
                <span className="font-medium tabular-nums">{signed(net)}</span>
              </div>
            </div>
          ) : (
            <p className="rounded-md border border-border bg-bright-moss/10 px-3 py-3 text-sm text-foreground">
              Every counted line matches the system. Nothing needs adjusting.
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cycle-count-note">Count note</Label>
            <Textarea
              id="cycle-count-note"
              value={draft.note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Optional. Added to every adjustment in this count, e.g. who counted and why."
              rows={2}
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={posting}
          >
            Keep counting
          </Button>
          <Button type="button" onClick={onPost} disabled={posting}>
            {posting
              ? 'Posting…'
              : adjustments.length > 0
                ? `Post ${adjustments.length.toLocaleString()} adjustment${adjustments.length === 1 ? '' : 's'}`
                : 'Finish count'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddLotDialog({
  open,
  onOpenChange,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (item: Item, lotNumber: string) => void;
}) {
  const [item, setItem] = useState<Item | null>(null);
  const [lotNumber, setLotNumber] = useState('');

  useEffect(() => {
    if (!open) {
      setItem(null);
      setLotNumber('');
    }
  }, [open]);

  const lotTracked = useCallback((it: Item) => it.is_lot_tracked, []);
  const trimmed = lotNumber.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (item && trimmed) onAdd(item, trimmed);
          }}
        >
          <DialogHeader>
            <DialogTitle>Add a lot found on the shelf</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <ItemPicker value={item?.id ?? null} onChange={setItem} filter={lotTracked} autoFocus />
            <div className="space-y-1.5">
              <Label htmlFor="cycle-count-lot">Lot number</Label>
              <Input
                id="cycle-count-lot"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                maxLength={120}
                className="h-12 font-mono"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!item || !trimmed}>
              Add to sheet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
